'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import '@/lib/altcha-locale'

import type { AltchaWidgetElement } from 'altcha'

import { captchaBypassEnabled } from '@/lib/captcha-bypass'

/**
 * One captcha wiring for every form. The widget mounts after hydration so the
 * Web Component's connectedCallback doesn't race the SSR diff, pre-warms a
 * payload on focus, and solve() falls back to a fresh verify at submit time
 * when the five-minute challenge has expired.
 */
export function useAltcha() {
  const ref = useRef<AltchaWidgetElement>(null)
  const [mounted, setMounted] = useState(false)
  const [payload, setPayload] = useState(captchaBypassEnabled ? 'bypass' : '')

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!captchaBypassEnabled) setMounted(true)
  }, [])

  useEffect(() => {
    if (captchaBypassEnabled || !mounted) return
    const el = ref.current
    if (!el) return
    const handleVerified = (e: Event) => {
      const detail = (e as CustomEvent<{ payload: string }>).detail
      setPayload(detail?.payload ?? '')
    }
    const clear = () => setPayload('')
    el.addEventListener('verified', handleVerified)
    el.addEventListener('expired', clear)
    el.addEventListener('error', clear)
    return () => {
      el.removeEventListener('verified', handleVerified)
      el.removeEventListener('expired', clear)
      el.removeEventListener('error', clear)
    }
  }, [mounted])

  const solve = useCallback(async () => {
    if (payload) return payload
    try {
      return (await ref.current?.verify())?.payload ?? ''
    } catch {
      return ''
    }
  }, [payload])

  const reset = useCallback(() => {
    ref.current?.reset()
    setPayload(captchaBypassEnabled ? 'bypass' : '')
  }, [])

  return { ref, mounted, solve, reset }
}

export function AltchaWidget({ captcha }: { captcha: ReturnType<typeof useAltcha> }) {
  if (captchaBypassEnabled) {
    return (
      <p className="text-center font-mono text-xs text-[#888]">verification disabled (local dev)</p>
    )
  }
  // The widget mounts on hydration. Holding its height keeps the send button
  // still on a slow phone, where hydration can land after the reader has
  // already scrolled to the form.
  if (!captcha.mounted) return <div className="min-h-[70px]" aria-hidden />
  return (
    <div
      style={
        {
          colorScheme: 'light',
          '--altcha-max-width': '100%',
          '--altcha-color-primary': '#8A2585',
          '--altcha-color-primary-content': '#ffffff',
          '--altcha-border-radius': '3px',
        } as React.CSSProperties
      }
    >
      <altcha-widget
        ref={captcha.ref}
        challenge="/api/captcha/challenge"
        auto="onfocus"
        configuration='{"hideFooter":true,"hideLogo":true}'
      />
    </div>
  )
}
